import {dailyRate} from 'utils/dailyRates';
import {daypartPairs, formatDayparts} from 'utils/dayparts';
import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useVisibilityChange, useDebounce } from "@uidotdev/usehooks";
import { gridFocusCellSelector, useGridApiRef } from '@mui/x-data-grid';
import {
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  IconButton,
  Input,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Popper,
  Select,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CalendarViewMonthOutlined as CalendarIcon,
  ContentCopy as CopyIcon,
  EditOutlined as EditIcon,
  MoreVert as MoreVertIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

import { useSession, useMisc } from 'store';
import {
  AppDataGrid,
  APP_DATA_GRID_COLUMN_PRESETS,
  APP_DATA_GRID_PAGE_SIZE,
  AppDataGridColumnSettingsButton,
  toast,
  Page,
  JobStatusFilter,
  JobNameWithScheduleLabel,
  isCrossDayDerived,
  useColumnVisibility,
  usePageSizePreference,
} from 'components';
import { BatchTodayDaypartsDialog, CustomMonitorDialog, Overview, JobState, LinkView, DeviceCheck, PresetDids, JobDailyReportPopup } from './components';
import { callApi, download, formatCompactDateRange } from 'utils';
import { allTrackers, trackerIcons } from 'consts';
import { t } from 'i18n';
import { getAlertMessages, getFocusedAlertMessages, getJobNameTooltipMessages } from './alertMessages';
import { getAlternateGroupRowIds } from './groupBanding';
import { getJobRowClassName, jobRowClasses } from './rowState';

const attentionRowCellSelector = [jobRowClasses.suspended, jobRowClasses.alerted]
  .flatMap(className => [
    `& .MuiDataGrid-row.${className} .MuiDataGrid-cell`,
    `& .MuiDataGrid-row.${className} .MuiDataGrid-cell *`,
  ])
  .join(', ');

const TrackData = ({percent, icon}) => (
  <Typography sx={{display: 'flex', alignItems: 'center', fontSize: 'inherit'}}>
    <img style={{height: '18px', width: '18px', marginRight: '3px'}} src={icon} alt="agent" />
    {percent}%
  </Typography>
);
const emptyAlerts = [];
const alertTitle = (messages) => (
  <React.Fragment>
    {messages.map(message => (<div key={message}>{message}</div>))}
  </React.Fragment>
);
const getDayparts = (job, ymd) => job.dayparts[ymd] || job.dayparts.default || ["00:00", "23:59"];

const columnHelper = {
  data: {},
  ymd: '',
  setData(misc, activeJobs) {
    this.data = {misc, activeJobs};
    this.ymd = dayjs().tz().format('YYYYMMDD');
  },
  dayPlan(job) {
    if (this.data.activeJobs?.includes(job)) {
      const overrun = dailyRate(job?.daily_overrun, this.ymd);
      const imps = isNaN(job.daily_imps[this.ymd]) ? job.daily_imps.default : job.daily_imps[this.ymd];
      if (imps > 0) {
        return (
          <Typography component="span" sx={{fontSize: 'inherit'}}>
            {imps}
            {overrun === null && '（溢出未配置）'}
            {overrun > 0 && (<Box component="span" sx={{ml: '3px', color: 'text.disabled'}}>+{overrun}%</Box>)}
          </Typography>
        );
      }
    }
    return '--';
  },
  dayparts(job) {
    return (
      <Stack sx={{py: 0.75, whiteSpace: 'nowrap'}}>
        {daypartPairs(getDayparts(job, this.ymd)).map((pair, index) => (
          <Box component="span" key={index}>{formatDayparts(pair)}</Box>
        ))}
      </Stack>
    );
  },
  miscData(cat, val) {
    return this.data.misc?.[cat]?.[val];
  },
};

function reportData(rt, field, alerts=emptyAlerts, suppressTooltip=false) {
  if (field === 'progress') {
    const messages = getAlertMessages(alerts, 'progress');
    const color = messages.length ? 'error.main' : 'inherit';
    const ps = rt?.progress;
    let node;
    if (!ps?.[1])
      node = (<Box component="span" sx={{color}}>--</Box>);
    else {
      const text = ps.map(p => (100*p).toFixed(1) + '%').join(' / ');
      const title = messages.length ? (
        <React.Fragment>
          <div>{text}</div>
          {alertTitle(messages)}
        </React.Fragment>
      ) : text;
      node = suppressTooltip ? (
        <Box component="span" sx={{color, display: 'block', width: '100%', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
          {text}
        </Box>
      ) : (
        <Tooltip title={title}>
          <Box component="span" sx={{color, display: 'block', width: '100%', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
            {text}
          </Box>
        </Tooltip>
      );
    }
    return messages.length && !ps?.[1] && !suppressTooltip ? (<Tooltip title={alertTitle(messages)}>{node}</Tooltip>) : node;
  }
  if (!rt) return '--';
  if (field === 'ctr') {
    const messages = getAlertMessages(alerts, 'ctr');
    const color = messages.length ? 'error.main' : 'inherit';
    const text = rt.imps > 0 ? (100*rt.clicks/rt.imps).toFixed(1) : '--';
    const node = (<Box component="span" sx={{color}}>{text}</Box>);
    return messages.length && !suppressTooltip ? (<Tooltip title={alertTitle(messages)}>{node}</Tooltip>) : node;
  }
  else if (field === 'landing') {
    const messages = getAlertMessages(alerts, ['landing', 'jump2', 'bounce', 'interaction', 'bind']);
    const color = messages.length ? 'error.main' : 'inherit';
    const text = rt.clicks > 0 ? (100*rt.landing/rt.clicks).toFixed(1) + '%' : '--';
    const node = (<Box component="span" sx={{color}}>{text}</Box>);
    return messages.length && !suppressTooltip ? (<Tooltip title={alertTitle(messages)}>{node}</Tooltip>) : node;
  }
  else if (field === 'track') {
    const t = allTrackers.map((name, i) => [rt.tracker_cnt[i], trackerIcons[name]]).find(a => a[0] > 0);
    if (!t)
      return '--';
    const p = (100*t[0]/rt.landing).toFixed(1);
    if (p === '0.0')
      return '--';
    return (<TrackData percent={p} icon={t[1]} />);
  }
  return rt[field] || 0;
}

const columns = {
  parent: {
    name: 'Group ID',
    value: row => row.parent,
  },
  id: {
    name: 'Job ID',
    value: row => row.id,
  },
  name: {
    name: 'Name',
    value: (row, _rt, alerts, suppressTooltip) => {
      const messages = getAlertMessages(alerts);
      const node = <JobNameWithScheduleLabel job={row} compact />;
      const namedNode = messages.length
        ? (<Box component="span" sx={{color: 'error.main', display: 'block', width: '100%', minWidth: 0}}>{node}</Box>)
        : node;
      const titles = getJobNameTooltipMessages(alerts, row.extra);
      return titles.length && !suppressTooltip
        ? (
          <Tooltip title={alertTitle(titles)}>
            <Box component="span" sx={{display: 'block', width: '100%', minWidth: 0}}>
              {namedNode}
            </Box>
          </Tooltip>
        )
        : node;
    },
  },
  ad_slot_name: {
    name: 'Ad Slot',
    value: row => row.ad_slot_name || '--',
  },
  type: {
    name: 'Type',
    value: row => {
      const ctr = dailyRate(row?.daily_ctr, columnHelper.ymd);
      return ctr === null ? '未配置' : t(ctr === 100 ? 'Click' : (ctr ? 'Mixed' : 'Imp'));
    },
  },
  schedule: {
    name: 'Start/End Date',
    value: (row, _rt, _alerts, suppressTooltip) => {
      const fullRange = row.start_date + ' ~ ' + row.end_date;
      const node = <span>{formatCompactDateRange(row.start_date, row.end_date)}</span>;
      if (suppressTooltip)
        return node;
      return (
        <Tooltip title={fullRange}>
          {node}
        </Tooltip>
      );
    },
  },
  dayparts: {
    name: 'Daypart',
    value: row => columnHelper.dayparts(row),
  },
  url: {
    name: 'Landing Url',
    value: row => (<LinkView destUrl={row.dest_url} trackers={row.trackers} />),
  },
  traffic: {
    name: 'Traffic',
    value: (row, _rt, _alerts, suppressTooltip) => {
      const value = columnHelper.miscData('traffic_curve', row.traffic_curve) || '';
      const node = (
        <Box component="span" sx={{display: 'block', width: '100%', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
          {value}
        </Box>
      );
      if (suppressTooltip)
        return node;
      return (
        <Tooltip title={value}>
          {node}
        </Tooltip>
      );
    },
  },
  plan: {
    name: 'Plan',
    value: row => columnHelper.dayPlan(row),
  },
  imps: {
    name: 'Imps',
    value: (_, rt) => reportData(rt, 'imps'),
  },
  clicks: {
    name: 'Clicks',
    value: (_, rt) => reportData(rt, 'clicks'),
  },
  ctr: {
    name: 'CTR',
    value: (_, rt, alerts, suppressTooltip) => reportData(rt, 'ctr', alerts, suppressTooltip),
  },
  landing: {
    name: 'Landing',
    value: (_, rt, alerts, suppressTooltip) => reportData(rt, 'landing', alerts, suppressTooltip),
  },
  progress: {
    name: 'Progress',
    value: (_, rt, alerts, suppressTooltip) => reportData(rt, 'progress', alerts, suppressTooltip),
  },
  track: {
    name: 'Track',
    value: (_, rt) => reportData(rt, 'track'),
  },
};

function loopGetRtReport(active, cb, delay) {
  if (!active)
    return;

  const getReport = () => callApi(`/reports/realtime`).then(rows =>
    cb(Object.fromEntries(rows.map(row => [row.ad, row])))
  ).catch(console.error);

  getReport();
  const itvl = setInterval(() => getReport(), delay);
  return () => clearInterval(itvl);
}

function loopGetJobAlerts(active, cb, delay, params) {
  if (!active)
    return;

  const query = params ? `?${params}` : '';
  const getAlerts = () => callApi(`/jobs/abnjobs${query}`).then(res => cb(res || {})).catch(console.error);
  getAlerts();

  const itvl = setInterval(() => getAlerts(), delay);
  return () => clearInterval(itvl);
}

const overflowMessage = () => '这是跨日排期的子任务，只支持修改数量，不能单独复制。';

const JobActions = memo(({row, isWarren, onSuspend, onShowJobState, onOpenMoreOps}) => {
  const id = row.id, suspend = row.suspend;
  const overflow = isCrossDayDerived(row);
  const blockOverflowEdit = () => toast(overflowMessage(row), 'info');

  return (
    <Box component="span" data-rowid={id} sx={{ whiteSpace: 'nowrap', '& .MuiButtonBase-root': {p: '3px'} }}>
        <IconButton onClick={() => onSuspend(!suspend, id)} size="small" disabled={overflow}>
          {suspend ? (<PlayIcon fontSize="inherit" />) : (<PauseIcon fontSize="inherit" />)}
        </IconButton>
      {isWarren && [
        <IconButton component={NavLink} to={`/jobs/${id}/edit`} size="small" key="edit">
          <EditIcon fontSize="inherit" />
        </IconButton>,
        overflow ? (
        <IconButton onClick={blockOverflowEdit} size="small" key="copy">
          <CopyIcon fontSize="inherit" />
        </IconButton>
        ) : (
        <IconButton component={NavLink} to={`/jobs/new?copy=${id}`} size="small" key="copy">
          <CopyIcon fontSize="inherit" />
        </IconButton>
        )
      ]}
        <IconButton onClick={() => onShowJobState(id)} size="small">
          <CalendarIcon fontSize="inherit" />
        </IconButton>
        <IconButton id={'jobops-'+id} onClick={onOpenMoreOps} size="small">
          <MoreVertIcon fontSize="inherit" />
        </IconButton>
    </Box>
  );
});

// enum for all model types
const ModalTypes = Object.freeze({
  OVERVIEW: 1,
  JOB_STATE: 2,
  SUSPEND_JOBS: 3,
  EXPORT_ALL: 4,
  PAGE_ACTION: 5,
  DEVICE: 8,
  CLEANUP: 9,
  PRESET_DIDS: 10,
  SSE_PRESET_DIDS: 11,
  DELETE_JOBS: 12,
  DAILY_REPORT: 13,
  TODAY_DAYPARTS: 14,
  CUSTOM_MONITORS: 15,
});

const adminCols = ['parent', 'id', 'name', 'ad_slot_name', 'type', 'schedule', 'dayparts', 'url', 'traffic', 'plan', 'imps', 'clicks', 'ctr', 'landing', 'progress'];
const otherCols = ['parent', 'id', 'name', 'ad_slot_name', 'type', 'schedule', 'dayparts', 'traffic', 'plan', 'imps', 'clicks', 'ctr', 'landing', 'progress'];
const lockedCols = new Set(['name', 'landing', 'progress']);

const defaultFilter = {gid: '', status: 'active', org: '', page: 0, limit: 100, q: ''};
const jobPageSizeOptions = [APP_DATA_GRID_PAGE_SIZE, 200, 300];
const dataGridColumnDefaults = {
  align: 'center',
  headerAlign: 'center',
  sortable: false,
  minWidth: 50,
  flex: 1,
};
const numericColumn = {
  align: 'right',
  headerAlign: 'right',
};
const columnSizing = {
  parent: {minWidth: 64, flex: 0.5},
  id: {minWidth: 50, flex: 0.45},
  name: {...APP_DATA_GRID_COLUMN_PRESETS.primaryText, minWidth: 140, flex: 2.2, align: 'center', headerAlign: 'center', cellClassName: 'JobList-nameCell'},
  ad_slot_name: {minWidth: 72, flex: 0.55, cellClassName: 'AppDataGrid-nowrapCell'},
  type: {minWidth: 56, flex: 0.45},
  schedule: {minWidth: 112, flex: 0.75},
  dayparts: {minWidth: 88, flex: 0.65, cellClassName: 'JobList-daypartsCell'},
  url: {minWidth: 112, flex: 0.75, cellClassName: 'AppDataGrid-nowrapCell'},
  traffic: {minWidth: 70, flex: 0.55, cellClassName: 'AppDataGrid-nowrapCell'},
  plan: {...numericColumn, minWidth: 104, flex: 1},
  imps: {...numericColumn, minWidth: 84, flex: 1.2, cellClassName: 'AppDataGrid-nowrapCell'},
  clicks: {...numericColumn, minWidth: 58, flex: 0.6},
  ctr: {...numericColumn, minWidth: 50, flex: 0.45},
  landing: {...numericColumn, minWidth: 88, flex: 0.7},
  progress: {...numericColumn, minWidth: 112, flex: 0.75, cellClassName: 'AppDataGrid-nowrapCell'},
  action: {minWidth: 114, flex: 0.55, cellClassName: 'AppDataGrid-actionCell'},
};

const JobList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const documentVisible = useVisibilityChange();
  const session = useSession((state) => state.session);
  const misc = useMisc((state) => state.data);
  const [list, setList] = useState({rows:[],total:0});
  const [selected, setSelected] = useState([]);
  const [reportDays, setReportDays] = useState(1);
  const [pageAction, setPageAction] = useState('');
  const [presetArgs, setPresetArgs] = useState({zip:'',filter:'',ios:''});
  const [modal, setModal] = useState(null);
  const [report, setReport] = useState({}); // realtime report
  const [jobAlerts, setJobAlerts] = useState({});
  const [focusedCell, setFocusedCell] = useState(null);
  const [moreOpsEl, setMoreOpsEl] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const jobGridApiRef = useGridApiRef();
  const [columnSettingsAnchorEl, setColumnSettingsAnchorEl] = useState(null);

  const rows = list.rows;
  const today = dayjs().tz().format('YYYY-MM-DD');
  const selectableRows = useMemo(() => rows.filter(row => !isCrossDayDerived(row)), [rows]);
  const selectableIds = useMemo(() => selectableRows.map(row => row.id), [selectableRows]);
  const alternateGroupRowIds = useMemo(() => getAlternateGroupRowIds(rows), [rows]);
  const getRowClassName = useCallback(
    ({row}) => getJobRowClassName(row, jobAlerts, alternateGroupRowIds),
    [alternateGroupRowIds, jobAlerts]
  );
  const handleGridStateChange = useCallback((state) => {
    const next = gridFocusCellSelector(state);
    setFocusedCell(current => {
      if (current?.id === next?.id && current?.field === next?.field)
        return current;
      return next ? {id: next.id, field: next.field} : null;
    });
  }, []);
  const focusedAlertMessages = getFocusedAlertMessages(focusedCell, jobAlerts);
  const focusedAlertAnchor = focusedAlertMessages.length && focusedCell
    ? jobGridApiRef.current?.getCellElement?.(focusedCell.id, focusedCell.field)
    : null;
  const focusedAlertCellKey = focusedAlertAnchor
    ? `${focusedCell.id}:${focusedCell.field}`
    : '';
  const isAdmin = session.role === 'admin';
  const isWarren = session.uid === 2;
  const { pageSize, onPageSizeChange } = usePageSizePreference({
    tableId: 'job-list',
    userId: session.uid,
    options: jobPageSizeOptions,
    defaultPageSize: APP_DATA_GRID_PAGE_SIZE,
  });
  const filter = Object.fromEntries(
    ['gid', 'status', 'org', 'q'].map(k => [k, searchParams.get(k) || defaultFilter[k]])
  );

  const updateSearchParam = (k, v) => {
    setSearchParams((prev) => {
      v !== defaultFilter[k] ? prev.set(k, v) : prev.delete(k);
      k !== 'page' && prev.delete('page');
      prev.delete('limit');
      return prev;
    });
  };

  const q = useDebounce(filter.q, 1500);
  const usp = Object.entries({...filter, q})
    .reduce((pv, [k, v]) => {v && pv.set(k, v); return pv}, new URLSearchParams())
    .toString();
  const alertParams = Object.entries({
    ...filter,
    q,
    page: searchParams.get('page') || defaultFilter.page,
    limit: pageSize,
  }).reduce((pv, [k, v]) => {v && pv.set(k, v); return pv}, new URLSearchParams()).toString();
  const dataUrl = usp ? `/jobs?${usp}` : '/jobs';

  const reloadJobs = useCallback(() => setRefresh(prev => prev-1), []);

  const handleSelectedChange = useCallback((value) => setSelected(prev => {
    if (Array.isArray(value)) return value.filter(id => selectableIds.includes(id));
    const ev = value;
    const t = ev.target, v = Number(t.value);
    if (!selectableIds.includes(v)) return prev;
    const next = [...prev];
    const i = next.indexOf(v);
    if (t.checked) {
      i === -1 && next.push(v);
    } else {
      i >= 0 && next.splice(i, 1);
    }
    return next;
  }), [selectableIds]);

  const selectedSelectableCount = selected.filter(id => selectableIds.includes(id)).length;
  const selectedTodayJobs = useMemo(
    () => selectableRows.filter(job => selected.includes(job.id) && job.start_date <= today && today <= job.end_date),
    [selectableRows, selected, today]
  );
  const excludedTodayCount = selectedSelectableCount - selectedTodayJobs.length;

  const exportSelected = (target) => {
    const selectedIds = selected.filter(id => selectableIds.includes(id));
    if (!selectedIds.length) return;
    const ids = selectedIds.join(',');
    let url;
    if (target === 'jobs') {
      url = `/jobs/export?ids=${ids}`;
    } else if (target.startsWith('report-')) {
      // report-daily/report-hourly/report-region
      url = `/reports/export?type=${target.substring(7)}&ids=${ids}`;
    }
    download(url, target);
    setSelected([]);
  };

  const handleOpenMoreOps = useCallback((ev) => setMoreOpsEl(ev.currentTarget), []);
  const handleCloseMoreOps = useCallback(() => setTimeout(() => setMoreOpsEl(null), 100), []);
  const moreopsElId = moreOpsEl?.id || '';
  const moreopsJob = moreopsElId.startsWith('jobops-') ? rows.find(row => row.id === Number(moreopsElId.substring(7))) : null;
  const moreopsOverflow = isCrossDayDerived(moreopsJob);

  const clickMenu = (op) => {
    if (op === ModalTypes.PRESET_DIDS) {
      setPresetArgs(prev => ({...prev, zip: `${moreopsJob.fc_id}.zip`}));
    }
    setModal([op, moreopsJob]);
  };
  const blockMoreOpsOverflowEdit = () => moreopsJob && toast(overflowMessage(moreopsJob), 'info');

  const openOverview = useCallback(() => setModal([ModalTypes.OVERVIEW]), []);
  const showJobState = useCallback((id) => setModal([ModalTypes.JOB_STATE, id]), []);
  const confirmSuspend = useCallback((suspend, id) => setModal([ModalTypes.SUSPEND_JOBS, [suspend, id]]), []);
  const selectExportDays = useCallback(() => setModal([ModalTypes.EXPORT_ALL]), []);
  const batchSetTodayDayparts = useCallback(() => setModal([ModalTypes.TODAY_DAYPARTS]), []);
  const batchSetPageAction = useCallback(() => setModal([ModalTypes.PAGE_ACTION]), []);
  const deleteJobs = useCallback(() => setModal([ModalTypes.DELETE_JOBS]), []);
  const closeModal = useCallback(() => setModal(null), []);
  const handleChangeReportDays = useCallback((ev) => setReportDays(ev.target.value), []);
  const handleSetPageact = useCallback((ev) => setPageAction(ev.target.value), []);
  //const handleSetPreset = useCallback((ev) => setPresetArgs(prev => ({[ev.target.name]: ev.target.value, ...prev})), []);
  const handleSetPreset = useCallback((ev) => setPresetArgs(prev => {
    // dynamic key not works, why?
    const o = {...prev};
    o[ev.target.name] = ev.target.value;
    return o;
  }), []);

  const handleTodayDaypartsSaved = useCallback((res) => {
    setModal(null);
    setSelected([]);
    reloadJobs();
    toast(`已统一更新 ${res.updated} 个任务的当天时段`, 'success');
  }, [reloadJobs]);

  let modalType, modalData, dialogContent;
  if (modal) {
    const [type, args] = modal;
    modalType = type;
    const job = typeof args === 'number' ? rows.find(row => row.id === args) : args;
    if (type === ModalTypes.JOB_STATE) {
      modalData = { job, stats: report[job?.id] };
    } else if (type === ModalTypes.DAILY_REPORT) {
      modalData = job;
    } else if (type === ModalTypes.CUSTOM_MONITORS) {
      modalData = job;
    } else if (type === ModalTypes.DEVICE) {
      modalData = job;
    } else if (type === ModalTypes.SUSPEND_JOBS) {
      const suspend = args[0];
      const jobIds = args[1] ? [args[1]] : selected;
      const jobs = rows.filter(row => !isCrossDayDerived(row) && row.suspend !== suspend && jobIds.includes(row.id));
      const l = jobs.length;
      if (l === 0) {
        setModal(null);
      } else {
        let txt = suspend ? '确认暂停' : '确认继续';
        txt += l === 1 ? `任务 ${jobs[0].id}: ${jobs[0].name}?` : `所选 ${l} 个任务?`;
        dialogContent = (<DialogContentText>{txt}</DialogContentText>);
        modalData = { suspend, ids: jobs.map(job => job.id) };
      }
    } else if (type === ModalTypes.DELETE_JOBS) {
      const txt = `确认删除所选 ${selected.filter(id => selectableIds.includes(id)).length} 个任务?`;
      dialogContent = (<DialogContentText>{txt}</DialogContentText>);
    } else if (type === ModalTypes.EXPORT_ALL) {
      dialogContent = (
        <Box>
          <span style={{marginRight: '5px'}}>{t('Export last')}</span>
          <Select value={reportDays} size="small" onChange={handleChangeReportDays}>
            {[1,2,3,4,5,6,7].map((n) => (<MenuItem value={n} key={n}>{n} {t('days')}</MenuItem>))}
          </Select>
        </Box>
      );
    } else if (type === ModalTypes.PAGE_ACTION) {
      dialogContent = (
        <Box>
          <span style={{marginRight: '5px'}}>{t('Set Page Action')}: </span>
          <Input
            sx={{ml: '8px', p: 0, width: 150}}
            value={pageAction}
            size="small"
            onChange={handleSetPageact}
          />
        </Box>
      );
    } else if (type === ModalTypes.CLEANUP) {
      modalData = job.id;
      dialogContent = (<DialogContentText>{`确认清理任务 ${job.id}: ${job.name}?`}</DialogContentText>);
    } else if (type === ModalTypes.PRESET_DIDS) {
      modalData = job;
      dialogContent = (
        <Stack spacing={1}>
          <Typography variant="body2">
            <Box component="span" sx={{color: 'text.secondary', mr: 1}}>{t('Job Name')}:</Box>
            {job.id} - {job.name}
          </Typography>
          <div>
            <TextField 
              label={t('Zip File')}
              variant="standard"
              sx={{width: 250}}
              name="zip"
              value={presetArgs.zip}
              size="small"
              onChange={handleSetPreset}
            />
          </div>
          <div>
            <Select
              name="filter"
              value={presetArgs.filter}
              size="small"
              onChange={handleSetPreset}
              displayEmpty
            >
              <MenuItem value={''}>{t('All Devices')}</MenuItem>
              <MenuItem value={'$make eq "huawei"'}>Huawei</MenuItem>
              <MenuItem value={'$make eq "xiaomi"||$make eq "redmi"'}>Xiaomi</MenuItem>
            </Select>
          </div>
          <div>
            <TextField
              label={t('iOS Percent')}
              variant="standard"
              sx={{width: 250}}
              name="ios"
              type="number"
              value={presetArgs.ios}
              size="small"
              onChange={handleSetPreset}
              inputProps={{min: 0, max: 100, step: 1}}
              InputProps={{endAdornment: <InputAdornment position="end">%</InputAdornment>}}
            />
          </div>
        </Stack>
      );
    } else if (type === ModalTypes.SSE_PRESET_DIDS) {
      modalData = args;
    }
  }

  const confirmDialog = () => {
    if (modalType === ModalTypes.EXPORT_ALL) {
      const url = `/reports/export?type=allindays&days=${reportDays}`;
      download(url, `last-${reportDays}-days`);
      setModal(null);
    } else if (modalType === ModalTypes.CLEANUP) {
      callApi(`/jobs/cleanup?ids=${modalData}`).then((res) => {
        console.log(res);
        setModal(null);
        toast(JSON.stringify(res), 'success');
      }).catch(console.error);
    } else if (modalType === ModalTypes.PRESET_DIDS) {
      const qs = Object.entries(presetArgs).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
      callApi(`/jobs/${modalData.id}/presetdids?${qs}`).then((res) => {
        setPresetArgs({zip:'',filter:'',ios:''});
        setModal([ModalTypes.SSE_PRESET_DIDS, res]);
      }).catch(console.error);
    } else {
      let ids, data;
      if (modalType === ModalTypes.SUSPEND_JOBS) {
        ids = modalData?.ids;
        data = {suspend: modalData?.suspend};
      } else if (modalType === ModalTypes.PAGE_ACTION) {
        ids = selected.filter(id => selectableIds.includes(id));
        data = {ant_cfg: pageAction};
      } else if (modalType === ModalTypes.DELETE_JOBS) {
        ids = selected.filter(id => selectableIds.includes(id));
        data = {deleted: true};
      } else {
        return;
      }
      if (!ids?.length) return;
      const url = `/jobs/batch/update?ids=${ids.join(',')}`;
      callApi(url, 'PUT', data).then(() => {
        setModal(null);
        setPageAction('');
        setSelected([]);
        reloadJobs();
      }).catch(console.error);
    }
  };

  const activeJobs = useMemo(
    () => rows.filter(job => job.start_date <= today && today <= job.end_date),
    [rows, today]);

  const updateRtReport = activeJobs.length > 0 && documentVisible;
  useEffect(() => loopGetRtReport(updateRtReport, setReport, 60_000), [updateRtReport]);
  useEffect(() => loopGetJobAlerts(updateRtReport, setJobAlerts, 60_000, alertParams), [updateRtReport, alertParams]);

  // set helper data for column value display
  columnHelper.setData(misc, activeJobs);
  const baseColumns = isAdmin ? adminCols : otherCols;
  const visibilityColumns = useMemo(() => [
    ...baseColumns.map(col => ({
      field: col,
      hideable: !lockedCols.has(col),
      defaultHidden: col === 'id',
    })),
    {field: 'action', hideable: false},
  ], [baseColumns]);
  const gridColumns = useMemo(() => [
    ...baseColumns.map(col => ({
      ...dataGridColumnDefaults,
      ...columnSizing[col],
      field: col,
      headerName: t(columns[col].name),
      hideable: !lockedCols.has(col),
      renderCell: (params) => {
        const cellKey = `${params.row.id}:${col}`;
        return columns[col].value(
          params.row,
          report?.[params.row.id],
          jobAlerts?.[params.row.id] || emptyAlerts,
          focusedAlertCellKey === cellKey
        );
      },
    })),
    {
      ...dataGridColumnDefaults,
      ...columnSizing.action,
      field: 'action',
      headerName: t('Action'),
      hideable: false,
      renderCell: (params) => (
        <JobActions
          row={params.row}
          isWarren={isWarren}
          onSuspend={confirmSuspend}
          onShowJobState={showJobState}
          onOpenMoreOps={handleOpenMoreOps}
        />
      ),
    },
  ], [baseColumns, report, jobAlerts, focusedAlertCellKey, isWarren, confirmSuspend, showJobState, handleOpenMoreOps]);
  const {
    columnVisibilityModel,
    onColumnVisibilityModelChange,
  } = useColumnVisibility({
    columns: visibilityColumns,
    tableId: 'job-list',
    userId: session.uid,
  });
  const todayYmd = today.replaceAll('-', '');
  const getRowHeight = useCallback(
    ({model}) => columnVisibilityModel.dayparts !== false && getDayparts(model, todayYmd).length > 2 ? 'auto' : null,
    [columnVisibilityModel.dayparts, todayYmd]
  );
  const columnSettingsSlotProps = useMemo(() => (
    columnSettingsAnchorEl ? {
      panel: {
        anchorEl: columnSettingsAnchorEl,
        placement: 'bottom-end',
      },
    } : undefined
  ), [columnSettingsAnchorEl]);

  return (
    <Page title="Job List">
      <Toolbar sx={{mt: -1, gap: 1, flexWrap: 'wrap'}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, flexWrap: 'wrap'}}>
          <JobStatusFilter value={filter.status} onChange={(status) => updateSearchParam('status', status)} />
          {isAdmin && (
          <Select
            name="org"
            value={filter.org}
            size="small"
            onChange={(ev) => updateSearchParam('org', ev.target.value)}
            displayEmpty
            sx={{ minWidth: 120, '& .MuiSelect-select': {py: '6px'} }}
          >
            <MenuItem value={''}>{t('All Orgs')}</MenuItem>
            {Object.entries(misc.org || {}).map(([val, display]) => (
            <MenuItem value={val} key={val}>{display}</MenuItem>
            ))}
          </Select>
          )}
        </Box>
        <Input
          sx={{p: '2px', mr: 1, width: 160}}
          name="q"
          value={filter.q}
          size="small"
          onChange={(ev) => updateSearchParam('q', ev.target.value)}
          inputProps={{ style: {marginLeft: '-5px'} }}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          }
        />
        {isAdmin && (<Button color="inherit" onClick={openOverview}>Overview</Button>)}
        <Button color="inherit" onClick={reloadJobs}>{t('Reload')}</Button>
        <AppDataGridColumnSettingsButton
          apiRef={jobGridApiRef}
          buttonId="job-list-column-settings-button"
          panelId="job-list-column-settings-panel"
          label={t('Column Settings')}
          ref={setColumnSettingsAnchorEl}
        />
        <IconButton color="inherit" id="globalops" onClick={handleOpenMoreOps}>
          <MoreVertIcon />
        </IconButton>
      </Toolbar>
      <Paper sx={{p: 1, pb: 0, m: 2, mt: -1, position: 'relative'}}>
        <AppDataGrid
          apiRef={jobGridApiRef}
          columns={gridColumns}
          idColumn="id"
          dataUrl={dataUrl}
          onDataChange={setList}
          selected={selected}
          onSelectedChange={handleSelectedChange}
          getRowClassName={getRowClassName}
          getRowHeight={getRowHeight}
          isRowSelectable={row => !isCrossDayDerived(row)}
          refresh={refresh}
          pageSize={pageSize}
          pageSizeOptions={jobPageSizeOptions}
          onPageSizeChange={onPageSizeChange}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={onColumnVisibilityModelChange}
          onStateChange={handleGridStateChange}
          showColumnSettings
          showColumnSettingsToolbar={false}
          slotProps={columnSettingsSlotProps}
          sx={{
            '& .JobList-row--alternateGroup': {
              backgroundColor: 'background.default',
            },
            [attentionRowCellSelector]: {
              color: 'error.main',
            },
            '& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight) > .MuiDataGrid-cell.JobList-nameCell': {
              overflow: 'hidden',
            },
            '& .MuiDataGrid-cell.JobList-nameCell > *': {
              minWidth: 0,
              width: '100%',
            },
            '& .MuiDataGrid-cell.JobList-daypartsCell': {
              px: 0.75,
            },
          }}
        />
        <Popper
          anchorEl={focusedAlertAnchor}
          open={Boolean(focusedAlertAnchor)}
          placement="bottom-start"
          modifiers={[{name: 'offset', options: {offset: [0, 4]}}]}
          sx={{zIndex: theme => theme.zIndex.tooltip, pointerEvents: 'none'}}
        >
          <Paper
            elevation={4}
            sx={theme => ({
              px: 1.25,
              py: 0.75,
              maxWidth: 400,
              overflowWrap: 'anywhere',
              backgroundColor: theme.vars.palette.app.tooltipBackground,
              border: `1px solid ${theme.vars.palette.app.tooltipBorder}`,
              color: theme.vars.palette.text.primary,
              fontSize: theme.typography.pxToRem(12),
            })}
          >
            {alertTitle(focusedAlertMessages)}
          </Paper>
        </Popper>
      </Paper>
      <Menu
        id="globalops-menu"
        anchorEl={moreOpsEl}
        open={moreopsElId === 'globalops'}
        onClose={handleCloseMoreOps}
        onClick={handleCloseMoreOps}
      >
        {isAdmin && [
        <MenuItem component={NavLink} to={`/jobs/new`} key="add">{t('Add Job')}</MenuItem>,
        <MenuItem component={NavLink} to={`/jobs/batchadd`} key="batchadd">{t('Batch Add Jobs')}</MenuItem>,
        <MenuItem component={NavLink} to={`/jobs/batchedit`} key="batchedit">{t('Batch Edit Jobs')}</MenuItem>,
        <MenuItem disabled={!selectedTodayJobs.length} onClick={batchSetTodayDayparts} key="today-dayparts">{t('Batch Edit Today Dayparts')}</MenuItem>,
        <MenuItem disabled={!selectedSelectableCount} onClick={batchSetPageAction} key="pageact">{t('Set Page Action')}</MenuItem>,
        <MenuItem disabled={!selectedSelectableCount} onClick={deleteJobs} key="delete">{t('Delete Selected Jobs')}</MenuItem>
        ]}
        <MenuItem disabled={!selectedSelectableCount} onClick={() => confirmSuspend(true, 0)}>{t('Pause Selected Jobs')}</MenuItem>
        <MenuItem disabled={!selectedSelectableCount} onClick={() => confirmSuspend(false, 0)}>{t('Resume Selected Jobs')}</MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem disabled={!selectedSelectableCount} onClick={() => exportSelected('jobs')}>{t('Export Selected Jobs')}</MenuItem>
        <MenuItem disabled={!selectedSelectableCount} onClick={() => exportSelected('report-daily')}>{t('Export Daily Report')}</MenuItem>
        <MenuItem disabled={!selectedSelectableCount} onClick={() => exportSelected('report-hourly')}>{t('Export Hourly Report')}</MenuItem>
        <MenuItem disabled={!selectedSelectableCount} onClick={() => exportSelected('report-region')}>{t('Export Region Report')}</MenuItem>
        <MenuItem onClick={selectExportDays}>{t('Export All Report')}</MenuItem>
      </Menu>
      <Menu
        id="jobops-menu"
        anchorEl={moreOpsEl}
        open={Boolean(moreopsJob)}
        onClose={handleCloseMoreOps}
        onClick={handleCloseMoreOps}
      >
        <MenuItem component={NavLink} to={`/jobs/${moreopsJob?.id}/edit`} key="edit">{t('Edit')}</MenuItem>
          {isAdmin && (
          moreopsOverflow ? (
          <MenuItem onClick={blockMoreOpsOverflowEdit} key="copy">{t('Copy')}</MenuItem>
          ) : (
          <MenuItem component={NavLink} to={`/jobs/new?copy=${moreopsJob?.id}`} key="copy">{t('Copy')}</MenuItem>
          )
        )}
        <MenuItem component={NavLink} to={`/jobs?gid=${moreopsJob?.parent}`}>{t('Same Order Jobs')}</MenuItem>
        {isAdmin && (
        <MenuItem onClick={() => clickMenu(ModalTypes.DAILY_REPORT)}>{t('Job Daily Report Popup')}</MenuItem>
        )}
        <MenuItem
          component={NavLink}
          to={`/reports/jobdaily?idtype=id&id=${moreopsJob?.id}&taskid=${moreopsJob?.id}&orderId=${moreopsJob?.parent}`}
        >
          {t('Job Daily Report')}
        </MenuItem>
        <MenuItem component={NavLink} to={`/reports/jobregion?idtype=id&id=${moreopsJob?.id}&taskid=${moreopsJob?.id}&orderId=${moreopsJob?.parent}`}>{t('Region Report')}</MenuItem>
        <MenuItem component={NavLink} to={`/reports/trackers?idtype=id&id=${moreopsJob?.id}&orderId=${moreopsJob?.parent}`}>{t('Track Stats')}</MenuItem>
        <MenuItem onClick={() => clickMenu(ModalTypes.CUSTOM_MONITORS)}>{t('Custom Monitor')}</MenuItem>
        {moreopsJob?.prepared > 0 && [
        <MenuItem onClick={() => clickMenu(ModalTypes.DEVICE)} key="reviewdid">{t('Review Device Id')}</MenuItem>,
        <MenuItem component={NavLink} to={`/jobs/devusage?groupid=${moreopsJob?.parent}`} key="didusage">{t('Device Id Usage')}</MenuItem>
        ]}
        {isAdmin && moreopsJob && moreopsJob.id === moreopsJob.fc_id && (
        <MenuItem onClick={() => clickMenu(ModalTypes.CLEANUP)}>{t('Cleanup')}</MenuItem>
        )}
        {isAdmin && moreopsJob && moreopsJob.id === moreopsJob.fc_id && (
        <MenuItem onClick={() => clickMenu(ModalTypes.PRESET_DIDS)}>{t(moreopsJob.prepared ? 'Append Preset Dids' : 'Preset Dids')}</MenuItem>
        )}
      </Menu>
      <Overview open={modalType === ModalTypes.OVERVIEW} onClose={closeModal} />
      {modalType === ModalTypes.JOB_STATE && (<JobState state={modalData} onClose={closeModal} />)}
      {isAdmin && (<JobDailyReportPopup job={modalType === ModalTypes.DAILY_REPORT ? modalData : null} onClose={closeModal} />)}
      <CustomMonitorDialog job={modalType === ModalTypes.CUSTOM_MONITORS ? modalData : null} onClose={closeModal} />
      <DeviceCheck job={modalType === ModalTypes.DEVICE ? modalData : null} onClose={closeModal} />
      <PresetDids args={modalType === ModalTypes.SSE_PRESET_DIDS ? modalData : null} onClose={closeModal} />
      {modalType === ModalTypes.TODAY_DAYPARTS && (
        <BatchTodayDaypartsDialog
          jobs={selectedTodayJobs}
          excludedCount={excludedTodayCount}
          onClose={closeModal}
          onSaved={handleTodayDaypartsSaved}
        />
      )}
    {Boolean(dialogContent) && (
      <Dialog open={true} onClose={closeModal}>
        <DialogContent>
          {dialogContent}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Cancel</Button>
          <Button onClick={confirmDialog}>Confirm</Button>
        </DialogActions>
      </Dialog>
    )}
    </Page>
  );
};

export default JobList;
